import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ScaleRounded } from '@mui/icons-material';

const money = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

const weightFormat = new Intl.NumberFormat('es-CL', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

function parseNumber(value) {
  const normalized = String(value ?? '').trim().replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
}

export default function WeightModal({ open, product, initialWeightKg = null, editing = false, onClose, onConfirm }) {
  const [unit, setUnit] = useState('g');
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setUnit('g');
    setValue(initialWeightKg != null && Number(initialWeightKg) > 0
      ? String(Math.round(Number(initialWeightKg) * 1000))
      : '');

    const timer = window.setTimeout(() => {
      inputRef.current?.focus?.();
      inputRef.current?.select?.();
    }, 90);
    return () => window.clearTimeout(timer);
  }, [initialWeightKg, open, product?.id]);

  const weightKg = useMemo(() => {
    const raw = parseNumber(value);
    if (raw <= 0) return 0;
    const kg = unit === 'kg' ? raw : raw / 1000;
    return Math.round(kg * 1000) / 1000;
  }, [unit, value]);

  const lineTotal = useMemo(
    () => Math.round(Number(product?.precioBruto || 0) * weightKg),
    [product?.precioBruto, weightKg]
  );

  const valid = weightKg > 0 && Number(product?.precioBruto || 0) > 0;

  const confirm = () => {
    if (!valid) return;
    onConfirm?.(weightKg);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      confirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3.5, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: '#eff6ff',
              color: 'primary.main',
            }}
          >
            <ScaleRounded />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={950}>
              {editing ? 'Editar peso' : 'Ingresar peso'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {product?.nombre || 'Producto pesable'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: '14px !important' }}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: 2.5,
              px: 1.4,
              py: 1.15,
              bgcolor: '#f8fbff',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>
                  PRECIO POR KG
                </Typography>
                <Typography variant="h6" fontWeight={1000} color="primary.main">
                  {money.format(product?.precioBruto || 0)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" noWrap>
                {product?.sku || product?.codigoBarras || ''}
              </Typography>
            </Stack>
          </Box>

          <Stack direction="row" spacing={0.8}>
            <TextField
              inputRef={inputRef}
              fullWidth
              label={unit === 'g' ? 'Peso en gramos' : 'Peso en kilos'}
              value={value}
              onChange={(event) => setValue(event.target.value.replace(/[^0-9.,]/g, ''))}
              onKeyDown={onKeyDown}
              inputProps={{ inputMode: 'decimal', style: { fontSize: 24, fontWeight: 900 } }}
              placeholder={unit === 'g' ? '1000' : '1'}
            />
            <Stack direction="row" spacing={0.4} sx={{ flexShrink: 0 }}>
              <Button
                variant={unit === 'g' ? 'contained' : 'outlined'}
                onClick={() => {
                  if (unit === 'kg' && parseNumber(value) > 0) setValue(String(Math.round(parseNumber(value) * 1000)));
                  setUnit('g');
                }}
                sx={{ minWidth: 52, fontWeight: 950 }}
              >
                g
              </Button>
              <Button
                variant={unit === 'kg' ? 'contained' : 'outlined'}
                onClick={() => {
                  if (unit === 'g' && parseNumber(value) > 0) setValue(String(parseNumber(value) / 1000).replace('.', ','));
                  setUnit('kg');
                }}
                sx={{ minWidth: 52, fontWeight: 950 }}
              >
                kg
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              borderRadius: 3,
              bgcolor: '#f4f8ff',
              border: '1px solid #dbeafe',
              px: 1.5,
              py: 1.25,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={850}>
                  PESO
                </Typography>
                <Typography variant="h6" fontWeight={950}>
                  {weightFormat.format(weightKg)} kg
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={850}>
                  TOTAL
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 1000, color: 'primary.main', lineHeight: 1 }}>
                  {money.format(lineTotal)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Puede ingresar 1000 g o 1 kg. Enter confirma el peso.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!valid} onClick={confirm} sx={{ minWidth: 130, fontWeight: 950 }}>
          {editing ? 'Actualizar' : 'Agregar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
